import { isNonNullableObject, } from "@knyt/artisan";
import { controlInput } from "./controlInput.js";
import { hold } from "./hold.js";
import { InputStateController } from "./InputStateController.js";
import { isReactiveControllerHost } from "./ReactiveController.js";
import { ReactiveControllerHostAdapter } from "./ReactiveControllerHostAdapter.js";
import { track, untrack } from "./tracking.js";
import { watch } from "./watch.js";
/**
 * This symbol is used to attach the controllable adapter
 * to an object that implements the `Controllable` interface.
 *
 * @remarks
 *
 * This symbol must be in the runtime-wide symbol registry
 * (`Symbol.for`) so that the reactive adapter can be
 * accessed from within different contexts.
 *
 * @internal scope: workspace
 */
export const __hostAdapter = Symbol.for("knyt.luthier.hostAdapter");
/**
 * @internal scope: workspace
 */
export class ControllableAdapter extends ReactiveControllerHostAdapter {
    /**
     * Adds a controller that subscribes to an observable and requests
     * an update on the host whenever the observable emits a value.
     *
     * @remarks
     *
     * A strong reference to the observable is retained to prevent
     * it from being garbage collected,
     */
    track(observable) {
        return track(this, observable);
    }
    /**
     * Unsubscribes the host from an observable,
     * stopping updates from being requested on new values,
     * and removes the strong reference to the observable.
     *
     * @remarks
     *
     * When called, if the observable is not strongly referenced by any other
     * part of the code, it may be garbage collected.
     */
    untrack(observable) {
        return untrack(this, observable);
    }
    /**
     * Creates an effect that tracks an observable and requests updates
     * whenever the observable emits a new value while the element is connected.
     */
    watch(observable) {
        return watch(this, observable);
    }
    /**
     * Creates a reference tracked by the host using the initial value.
     *
     * @remarks
     *
     * This is a simple way to create reactive state.
     * A reference is created with the initial value,
     * and the host subscribes to it. When the value changes,
     * a request for an update is made on the host.
     *
     * @public
     *
     * @see {@link Reference}
     * @see {@link track}
     */
    hold(initialValue, onUpdate) {
        return hold(this, initialValue, onUpdate);
    }
    /**
     * Creates a controller that manages the state of an input element.
     *
     * @beta This is a beta feature and may change in the future.
     */
    // TODO: Add  documentation for this method.
    controlInput(options) {
        return controlInput(this, options);
    }
    /**
     * Whether root element content is currently being inserted or updated.
     * This is used to prevent conflicting updates.
     */
    #isModifying = false;
    /**
     * Whether an update is pending for the root element.
     * This is used to queue updates when root element content is already being
     * inserted or updated.
     */
    get #isModificationPending() {
        return !!this.#pendingModification;
    }
    #pendingModification;
    #setupPendingModification() {
        if (!this.#pendingModification) {
            this.#pendingModification = Promise.withResolvers();
        }
        return this.#pendingModification.promise;
    }
    #flushPendingModification() {
        this.debugLog("flushPendingModifications called");
        const pendingModification = this.#pendingModification;
        this.#pendingModification = undefined;
        return {
            resolve: () => {
                this.debugLog("pendingModification.resolve called");
                pendingModification?.resolve();
            },
        };
    }
    /**
     * @internal scope: workspace
     */
    async stageModification({ shouldModify, modification, }) {
        this.debugLog("stageModification called");
        if (this.#isModifying) {
            this.debugLog("stageModification isUpdatePending = true");
            return this.#setupPendingModification();
        }
        this.#isModifying = true;
        if (shouldModify) {
            this.debugLog("stageModification: before update");
            await modification();
            this.debugLog("stageModification: after update");
        }
        else {
            // `shouldModify` is `false`, meaning the modification should not be applied,
            // this typically is set to false when the host element is not connected
            // to the DOM, so we don't apply the modification unnecessarily.
            //
            // In this case, we don't setup a pending modification, because the pending
            // modification will never be flushed if the element is never connected;
            // i.e. during SSR. Additionally, when the element is connected, it will be
            // mounted, which will request an update on the host.
            this.debugLog("stageModification: shouldModify = false (not connected)");
        }
        this.#isModifying = false;
        if (this.#isModificationPending) {
            this.debugLog("stageModification: isModificationPending = true");
            const pendingModification = this.#flushPendingModification();
            this.performPendingUpdate().then(pendingModification.resolve);
        }
        else {
            this.debugLog("stageModification: isModificationPending = false");
        }
    }
}
/**
 * Determines whether the input is a {@link Controllable}.
 *
 * @internal scope: workspace
 */
export function isControllable(value) {
    return (isNonNullableObject(value) &&
        __hostAdapter in value &&
        isReactiveControllerHost(value[__hostAdapter]));
}
/**
 * Assert that the object is a {@link Controllable}.
 *
 * @internal scope: workspace
 */
export function assertControllable(obj) {
    if (!isControllable(obj)) {
        throw new TypeError("Object is not controllable");
    }
}
const mixedConstructors = new WeakSet();
const propertyMembers = ["updateComplete"];
export function applyControllableMixin(Constructor, members = []) {
    if (mixedConstructors.has(Constructor))
        return;
    mixedConstructors.add(Constructor);
    const proto = Constructor.prototype;
    for (const memberName of members) {
        if (memberName in proto) {
            throw new Error(`Member "${memberName}" already exists on the prototype. Please rename the member.`);
        }
        if (propertyMembers.includes(memberName)) {
            Object.defineProperty(proto, memberName, {
                get() {
                    // TODO: Remove in production.
                    assertControllable(this);
                    return this[__hostAdapter][memberName];
                },
            });
            continue;
        }
        if (typeof ControllableAdapter.prototype[memberName] !== "function") {
            throw new Error(`Method "${memberName}" does not exist on the ControllableAdapter prototype.`);
        }
        proto[memberName] = function (...args) {
            // TODO: Remove in production.
            assertControllable(this);
            return this[__hostAdapter][memberName](...args);
        };
    }
}
